"use client";

import type { useChat } from "@ai-sdk/react";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import {
  type URLGroupPatternType,
  URLGroupPatternTypeLabels,
} from "../shared/url-group-pattern-type";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { UPDATE_SA_URL_GROUP_NAME } from "./constants";
import type { UpdateSearchAnalyticsUrlGroupInput } from "./schema";

const UrlGroupQuery = graphql(`
  query SaUrlGroupUpdateConfirm($id: ID!) {
    node(id: $id) {
      __typename
      ... on URLGroup {
        id
        name
        description
        op
        patterns {
          type
          pattern
        }
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function UpdateSearchAnalyticsUrlGroupConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();
  const isTarget =
    isToolUIPart(part) && getToolName(part) === UPDATE_SA_URL_GROUP_NAME;
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ??
    {}) as Partial<UpdateSearchAnalyticsUrlGroupInput>;
  const groupId = input.id ?? "";

  const [{ data, fetching }] = useQuery({
    query: UrlGroupQuery,
    variables: { id: groupId },
    pause: !groupId,
  });

  const group = data?.node?.__typename === "URLGroup" ? data.node : null;

  const newPatterns = input.patterns ?? [];
  const patternTypeLabel = URLGroupPatternTypeLabels as Record<
    URLGroupPatternType,
    unknown
  >;
  const patternCount = newPatterns.length;
  const previewLimit = 10;
  const patternPreview = newPatterns.slice(0, previewLimit);
  const remainingCount = Math.max(patternCount - previewLimit, 0);

  if (!isTarget || !isInputAvailable) return null;

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to update this Search Analytics URL group?</Trans>
      </div>
      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>Current name</Trans>:{" "}
          {fetching ? (
            <Skeleton className="inline-block h-4 w-16 align-middle" />
          ) : (
            <span className="font-medium">{group?.name ?? "Unknown"}</span>
          )}
        </li>
        {input.name ? (
          <li>
            <Trans>New name</Trans>:{" "}
            <span className="font-medium">{input.name}</span>
          </li>
        ) : null}
        {"description" in input ? (
          <li>
            <Trans>Description</Trans>:{" "}
            <span className="font-normal">{input.description ?? "(null)"}</span>
          </li>
        ) : null}
        {input.op ? (
          <li>
            <Trans>Logical operator</Trans>:{" "}
            <span className="font-medium">{input.op}</span>
          </li>
        ) : null}
        {patternCount ? (
          <li>
            <Trans>Patterns</Trans>:{" "}
            <span className="font-medium">{patternCount}</span>
            <div className="mt-1 space-y-1 rounded-md border p-2">
              {patternPreview.map((p, idx) => (
                <div
                  key={`${p.type}-${p.pattern}-${idx}`}
                  className="flex items-start gap-2"
                >
                  <Badge variant="secondary" className="shrink-0">
                    {i18n._(
                      patternTypeLabel[
                        p.type as URLGroupPatternType
                      ] as Parameters<typeof i18n._>[0],
                    ) ?? p.type}
                  </Badge>
                  <span className="break-all font-mono text-xs">
                    {p.pattern}
                  </span>
                </div>
              ))}
              {remainingCount > 0 ? (
                <div className="text-muted-foreground text-xs">
                  <Trans>{remainingCount} more not shown</Trans>
                </div>
              ) : null}
            </div>
          </li>
        ) : null}
      </ul>

      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={UPDATE_SA_URL_GROUP_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={UPDATE_SA_URL_GROUP_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
